/**
 * Conversation State Machine
 * Processes user input and manages conversation flow
 */

import type { ConversationStep, ConversationState, Message, LeadData } from '../types'
import { getStepDefinition } from './steps'
import { generateUUID } from '../utils/uuid'
import { isAiQuestion } from './aiDetection'

export interface StateMachineResult {
  nextStep: ConversationStep
  botMessage: Message
  updatedLeadData: Partial<LeadData>
  validationError?: string
}

/**
 * Process user input and determine next conversation step
 */
export function processUserInput(
  currentStep: ConversationStep,
  userInput: string,
  currentState: ConversationState
): StateMachineResult {
  const stepDef = getStepDefinition(currentStep)

  // Check if user is asking if bot is AI (only on text input steps)
  if (stepDef.inputType === 'text' && isAiQuestion(userInput)) {
    // Store the question in lead data
    const aiQuestionData: Partial<LeadData> = {
      userQuestion: userInput,
    }

    // Redirect to AI deflection step
    const deflectStepDef = getStepDefinition('ai_question_deflect')
    const deflectMessage = deflectStepDef.message(currentState)

    const normalizedOptions = deflectStepDef.options?.map((opt) => ({
      label: opt.label,
      value: opt.value,
      ...(opt.emoji ? { emoji: opt.emoji } : {}),
    }))

    const botMessage = createBotMessage(deflectMessage, currentState, normalizedOptions)

    return {
      nextStep: 'ai_question_deflect',
      botMessage,
      updatedLeadData: aiQuestionData,
    }
  }

  // Validate input if validator exists
  if (stepDef.validator) {
    const error = stepDef.validator(userInput)
    if (error) {
      // Return validation error - stay on same step
      return {
        nextStep: currentStep,
        botMessage: createBotMessage(error, currentState),
        updatedLeadData: {},
        validationError: error,
      }
    }
  }

  // Update lead data based on current step
  const updatedLeadData = updateLeadDataForStep(currentStep, userInput, currentState)

  // Determine next step
  const nextStep = stepDef.nextStep(userInput, {
    ...currentState,
    leadData: { ...currentState.leadData, ...updatedLeadData },
  })

  // Generate bot message for next step
  const nextStepDef = getStepDefinition(nextStep)
  const botMessageText = nextStepDef.message({
    ...currentState,
    step: nextStep,
    leadData: { ...currentState.leadData, ...updatedLeadData },
  })

  // Normalize options to match ButtonOption type exactly
  const normalizedOptions = nextStepDef.options?.map((opt) => ({
    label: opt.label,
    value: opt.value,
    ...(opt.emoji ? { emoji: opt.emoji } : {}),
  }))

  const botMessage = createBotMessage(botMessageText, currentState, normalizedOptions)

  return {
    nextStep,
    botMessage,
    updatedLeadData,
  }
}

/**
 * Update lead data based on the current step and user response
 */
function updateLeadDataForStep(
  step: ConversationStep,
  value: string,
  state: ConversationState
): Partial<LeadData> {
  const updates: Record<string, unknown> = {}

  switch (step) {
    // Buy or sell choice
    case 'welcome':
    case 'buy_or_sell':
      if (value === 'buying' || value === 'buy') {
        updates.type = 'buyer'
      } else if (value === 'selling' || value === 'sell') {
        updates.type = 'seller'
      }
      break

    // Buyer path
    case 'buyer_price_range':
      updates.buyerPriceRange = value
      break

    case 'buyer_cities':
      updates.buyerCities = value
      break

    case 'buyer_preapproval':
      updates.buyerPreapproval = value
      break

    case 'buyer_timeline':
      updates.buyerTimeline = value
      // Check for HOT LEAD (buyer)
      if (
        value === 'ready_now' &&
        state.leadData.buyerPreapproval === 'yes_preapproved'
      ) {
        updates.hotLead = true
      }
      break

    case 'buyer_push_calendar':
    case 'buyer_push_calendar_again':
      if (value === 'book_it') {
        updates.wantsCalendar = true
        updates.calendarPushCount = (state.calendarPushCount || 0) + 1
      }
      break

    case 'buyer_collect_name':
    case 'seller_collect_name':
      updates.name = value.trim()
      break

    case 'buyer_collect_phone':
    case 'seller_collect_phone':
      // Normalize phone number
      updates.phone = value.replace(/\D/g, '')
      break

    case 'buyer_collect_email':
    case 'seller_collect_email':
      updates.email = value.trim().toLowerCase()
      break

    // Seller path
    case 'seller_address':
      updates.sellerAddress = value.trim()
      break

    case 'seller_timeline':
      updates.sellerTimeline = value
      // Check for HOT LEAD (seller)
      if (value === 'asap') {
        updates.hotLead = true
      }
      break

    case 'seller_agent_experience':
      updates.sellerAgentExperience = value
      break

    case 'seller_push_calendar':
    case 'seller_push_calendar_again':
      if (value === 'book_it') {
        updates.wantsCalendar = true
        updates.calendarPushCount = (state.calendarPushCount || 0) + 1
      }
      break

    // Calendly booking completed
    case 'buyer_show_calendly':
    case 'seller_show_calendly':
      updates.scheduledCall = true
      updates.calendlyEventId = value // Calendly event ID
      break

    // Question handling
    case 'question_input':
      updates.userQuestion = value.trim()
      break

    case 'question_deflect':
    case 'ai_question_deflect':
      if (value === 'book_it') {
        updates.wantsCalendar = true
      }
      break
  }

  return updates as Partial<LeadData>
}

/**
 * Create a bot message
 */
export function createBotMessage(
  text: string,
  _state: ConversationState,
  buttons?: Array<{ label: string; value: string; emoji?: string }>
): Message {
  return {
    id: generateUUID(),
    type: 'bot',
    text,
    timestamp: new Date(),
    buttons: buttons?.map((btn) => ({
      label: btn.label,
      value: btn.value,
      ...(btn.emoji ? { emoji: btn.emoji } : {}),
    })),
  }
}

/**
 * Create a user message
 */
export function createUserMessage(text: string): Message {
  return {
    id: generateUUID(),
    type: 'user',
    text,
    timestamp: new Date(),
  }
}

/**
 * Initialize a new conversation
 */
export function initializeConversation(
  clientId: string,
  botName: string,
  conversationMode?: string
): ConversationState {
  // Use freeform_welcome for storefront, otherwise default welcome
  const initialStep: ConversationStep = conversationMode === 'freeform' ? 'freeform_welcome' : 'welcome'
  const welcomeStep = getStepDefinition(initialStep)

  const initialState: ConversationState = {
    conversationId: generateUUID(),
    step: initialStep,
    leadData: { clientId },
    botName,
    calendarPushCount: 0,
    messages: [],
    startedAt: new Date(),
  }

  // Add welcome message
  const normalizedWelcomeOptions = welcomeStep.options?.map((opt) => ({
    label: opt.label,
    value: opt.value,
    ...(opt.emoji ? { emoji: opt.emoji } : {}),
  }))

  const welcomeMessage = createBotMessage(
    welcomeStep.message(initialState),
    initialState,
    normalizedWelcomeOptions
  )

  initialState.messages.push(welcomeMessage)

  return initialState
}
